'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  created_at: string;
}

export interface Company {
  id: string;
  name: string;
  industry: string;
  tax_id: string;
  address: string;
  phone: string;
  email: string;
  logo_url: string;
  owner_id: string;
  created_at: string;
}

export interface CompanyMember {
  id: string;
  company_id: string;
  user_id: string;
  role: RoleType;
  custom_permissions: Record<string, boolean>;
  is_active: boolean;
  joined_at: string;
  user_profiles?: UserProfile;
}

export type RoleType =
  | 'superadmin' |'administrador' |'contador' |'auxiliar_contable' |'facturador' |'ventas' |'compras' |'tesoreria' |'gerente' |'auditor' |'cliente_portal' |'proveedor_portal';

export interface CompanyWithRole extends Company {
  member_role: RoleType;
  custom_permissions: Record<string, boolean>;
}

export interface RoleDefinition {
  key: RoleType;
  label: string;
  emoji: string;
  description: string;
  permissions: Record<string, boolean>;
}

// All 12 roles with their exact module permissions
export const ROLES: RoleDefinition[] = [
  {
    key: 'superadmin',
    label: 'Superadmin',
    emoji: '🧠',
    description: 'Acceso total a todos los módulos',
    permissions: {
      dashboard: true, transactions: true, sales: true, expenses: true,
      customers: true, reports: true, payroll: true, time: true,
      projects: true, taxes: true, accountant: true, apps: true, users: true,
    },
  },
  {
    key: 'administrador',
    label: 'Administrador',
    emoji: '⚙️',
    description: 'Acceso completo excepto Mi Contador',
    permissions: {
      dashboard: true, transactions: true, sales: true, expenses: true,
      customers: true, reports: true, payroll: true, time: true,
      projects: true, taxes: true, accountant: false, apps: true, users: true,
    },
  },
  {
    key: 'contador',
    label: 'Contador',
    emoji: '📊',
    description: 'Contabilidad, impuestos y reportes',
    permissions: {
      dashboard: true, transactions: true, sales: true, expenses: true,
      customers: true, reports: true, payroll: false, time: false,
      projects: false, taxes: true, accountant: true, apps: false, users: false,
    },
  },
  {
    key: 'auxiliar_contable',
    label: 'Auxiliar Contable',
    emoji: '📘',
    description: 'Paneles, transacciones, gastos y clientes',
    permissions: {
      dashboard: true, transactions: true, sales: false, expenses: true,
      customers: true, reports: false, payroll: false, time: false,
      projects: false, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'facturador',
    label: 'Facturador',
    emoji: '🧾',
    description: 'Paneles, ventas y clientes',
    permissions: {
      dashboard: true, transactions: false, sales: true, expenses: false,
      customers: true, reports: false, payroll: false, time: false,
      projects: false, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'ventas',
    label: 'Ventas',
    emoji: '💼',
    description: 'Paneles, ventas, clientes y proyectos',
    permissions: {
      dashboard: true, transactions: false, sales: true, expenses: false,
      customers: true, reports: false, payroll: false, time: false,
      projects: true, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'compras',
    label: 'Compras',
    emoji: '🛒',
    description: 'Paneles, gastos y clientes/proveedores',
    permissions: {
      dashboard: true, transactions: false, sales: false, expenses: true,
      customers: true, reports: false, payroll: false, time: false,
      projects: false, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'tesoreria',
    label: 'Tesorería',
    emoji: '💳',
    description: 'Paneles, transacciones, gastos e informes',
    permissions: {
      dashboard: true, transactions: true, sales: false, expenses: true,
      customers: false, reports: true, payroll: false, time: false,
      projects: false, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'gerente',
    label: 'Gerente / Supervisor',
    emoji: '📈',
    description: 'Paneles, informes y proyectos',
    permissions: {
      dashboard: true, transactions: false, sales: false, expenses: false,
      customers: false, reports: true, payroll: false, time: false,
      projects: true, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'auditor',
    label: 'Auditor',
    emoji: '🔍',
    description: 'Lectura de paneles, transacciones, ventas, gastos, clientes, informes e impuestos',
    permissions: {
      dashboard: true, transactions: true, sales: true, expenses: true,
      customers: true, reports: true, payroll: false, time: false,
      projects: false, taxes: true, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'cliente_portal',
    label: 'Cliente (portal)',
    emoji: '👤',
    description: 'Solo ver sus propias facturas en Ventas',
    permissions: {
      dashboard: false, transactions: false, sales: true, expenses: false,
      customers: false, reports: false, payroll: false, time: false,
      projects: false, taxes: false, accountant: false, apps: false, users: false,
    },
  },
  {
    key: 'proveedor_portal',
    label: 'Proveedor (portal)',
    emoji: '🏢',
    description: 'Solo ver pagos recibidos en Gastos',
    permissions: {
      dashboard: false, transactions: false, sales: false, expenses: true,
      customers: false, reports: false, payroll: false, time: false,
      projects: false, taxes: false, accountant: false, apps: false, users: false,
    },
  },
];

// Build ROLE_PERMISSIONS map for quick lookup
export const ROLE_PERMISSIONS: Record<RoleType, Record<string, boolean>> = Object.fromEntries(
  ROLES.map(r => [r.key, r.permissions])
) as Record<RoleType, Record<string, boolean>>;

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  profile: UserProfile | null;
  companies: CompanyWithRole[];
  activeCompany: CompanyWithRole | null;
  activeRole: RoleType | null;
  permissions: Record<string, boolean>;
  signUp: (email: string, password: string, fullName: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  setActiveCompany: (company: CompanyWithRole) => void;
  refreshCompanies: () => Promise<void>;
  hasPermission: (area: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [companies, setCompanies] = useState<CompanyWithRole[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<CompanyWithRole | null>(null);
  const initializingRef = useRef(false);
  const initializedRef = useRef(false);
  const initSessionRef = useRef<(() => Promise<void>) | null>(null);
  const lastLoadedUserIdRef = useRef<string | null>(null);

  const loadProfile = useCallback(async (userId: string, userEmail: string) => {
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) {
      setProfile(data as UserProfile);
    } else {
      setProfile({
        id: userId,
        email: userEmail,
        full_name: userEmail.split('@')[0],
        avatar_url: '',
        created_at: new Date().toISOString(),
      });
    }
  }, []);

  const loadCompanies = useCallback(async (userId: string): Promise<CompanyWithRole[]> => {
    const { data: members } = await supabase
      .from('company_members')
      .select('*, companies(*)')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (members && members.length > 0) {
      const result: CompanyWithRole[] = members
        .filter((m: any) => m.companies)
        .map((m: any) => {
          // Map DB roles (administrador/estandar/personalizado) to extended roles
          let mappedRole: RoleType = 'administrador';
          const dbRole = m.role as string;
          if (dbRole === 'administrador') mappedRole = 'superadmin';
          else if (dbRole === 'estandar') mappedRole = 'contador';
          else if (dbRole === 'personalizado') mappedRole = 'auxiliar_contable';
          else if (ROLES.find(r => r.key === dbRole)) mappedRole = dbRole as RoleType;

          return {
            ...m.companies,
            member_role: mappedRole,
            custom_permissions: m.custom_permissions || {},
          };
        });
      return result;
    }

    // Fallback: check if user owns any companies directly
    const { data: ownedCompanies } = await supabase
      .from('companies')
      .select('*')
      .eq('owner_id', userId);

    if (ownedCompanies && ownedCompanies.length > 0) {
      return ownedCompanies.map((c: any) => ({
        ...c,
        member_role: 'superadmin' as RoleType,
        custom_permissions: {},
      }));
    }

    return [];
  }, []);

  const initSession = useCallback(async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    setLoading(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.user) {
        lastLoadedUserIdRef.current = currentSession.user.id;
        setSession(currentSession);
        setUser(currentSession.user);
        await loadProfile(currentSession.user.id, currentSession.user.email || '');
        const userCompanies = await loadCompanies(currentSession.user.id);
        setCompanies(userCompanies);

        // Restore last active company
        const savedCompanyId = typeof window !== 'undefined' ? localStorage.getItem('contacien_active_company')
          : null;
        const savedCompany = savedCompanyId
          ? userCompanies.find(c => c.id === savedCompanyId)
          : null;
        setActiveCompanyState(savedCompany || userCompanies[0] || null);
      }
      initializedRef.current = true;
    } catch (err) {
      console.error('Session init error:', err);
    }
    setLoading(false);
    initializingRef.current = false;
  }, [loadProfile, loadCompanies]);

  useEffect(() => {
    initSession();
    initSessionRef.current = initSession;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      try {
        if (event === 'TOKEN_REFRESHED') {
          // Only update session and user — do NOT reload profile or companies
          if (newSession?.user) {
            setSession(newSession);
            setUser(newSession.user);
          }
          return;
        }

        if (event === 'SIGNED_IN' && newSession?.user) {
          // Skip if initSession already loaded data for this user
          if (lastLoadedUserIdRef.current === newSession.user.id) return;
          lastLoadedUserIdRef.current = newSession.user.id;
          setSession(newSession);
          setUser(newSession.user);
          await loadProfile(newSession.user.id, newSession.user.email || '');
          const userCompanies = await loadCompanies(newSession.user.id);
          setCompanies(userCompanies);
          const savedCompanyId = typeof window !== 'undefined' ? localStorage.getItem('contacien_active_company')
            : null;
          const savedCompany = savedCompanyId
            ? userCompanies.find(c => c.id === savedCompanyId)
            : null;
          setActiveCompanyState(savedCompany || userCompanies[0] || null);
          initializedRef.current = true;
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          lastLoadedUserIdRef.current = null;
          setUser(null);
          setSession(null);
          setProfile(null);
          setCompanies([]);
          setActiveCompanyState(null);
          initializedRef.current = false;
          setLoading(false);
        }
      } catch (err) {
        console.error('onAuthStateChange error:', err);
        // Clear all auth state and redirect to login
        lastLoadedUserIdRef.current = null;
        setUser(null);
        setSession(null);
        setProfile(null);
        setCompanies([]);
        setActiveCompanyState(null);
        initializedRef.current = false;
        setLoading(false);
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) throw error;
      return data;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    if (typeof window !== 'undefined') {
      localStorage.removeItem('contacien_active_company');
    }
  };

  const setActiveCompany = (company: CompanyWithRole) => {
    setActiveCompanyState(company);
    if (typeof window !== 'undefined') {
      localStorage.setItem('contacien_active_company', company.id);
    }
  };

  const refreshCompanies = async () => {
    if (!user) return;
    const userCompanies = await loadCompanies(user.id);
    setCompanies(userCompanies);
    if (!activeCompany && userCompanies.length > 0) {
      setActiveCompanyState(userCompanies[0]);
    }
  };

  const activeRole = activeCompany?.member_role ?? null;

  const permissions: Record<string, boolean> = (() => {
    if (!activeRole) return {};
    if (activeRole === 'superadmin') return ROLE_PERMISSIONS['superadmin'];
    return ROLE_PERMISSIONS[activeRole] || {};
  })();

  const hasPermission = (area: string): boolean => {
    if (!activeRole) return false;
    if (activeRole === 'superadmin') return true;
    return permissions[area] === true;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      profile,
      companies,
      activeCompany,
      activeRole,
      permissions,
      signUp,
      signIn,
      signOut,
      setActiveCompany,
      refreshCompanies,
      hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
